/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo } from "react";
import { Table } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { useTranslation } from "react-i18next";
import { IEquipment } from "pages/thermography/model/equipment.model";
import { IPlant as PowerStation } from "pages/thermography/model/plant.model";
import { DatabaseService } from "../stores/DatabaseService";
import { IInspectionRecord } from "../model/inspection-record.model";
import { IInspectionRecordGroup } from "../model/inspection-record-group.model";
import { getDateLocaleConfig } from "i18n/locale";

type SelectChange = React.ChangeEvent<HTMLSelectElement>;
type InputChange = React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>;

interface ThermalRegistrationDataInfoProps {
    mode: string | null;
    anomalyRecord: any;

    powerStations: PowerStation[];
    equipmentsFiltered: IEquipment[];
    inspectionRoutesFiltered: IInspectionRecord[];
    thermographers: any[];

    equipmentType: string;

    onSelectChange?: (e: SelectChange) => void;
    onInputChange?: (e: InputChange) => void;
}

const labelCellStyle: React.CSSProperties = {
    width: 160,
    whiteSpace: "nowrap",
    verticalAlign: "middle",
};

const valueTextStyle: React.CSSProperties = {
    display: "block",
    width: "100%",
    fontSize: "10px",
    lineHeight: 1.5,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
};

// Função recursiva para buscar o grupo que contém o equipamento
const findGroupByEquipmentId = (groups: IInspectionRecordGroup[] | undefined, equipmentId: string): IInspectionRecordGroup | null => {
    if (!groups || groups.length === 0) return null;

    for (const group of groups) {
        // Verifica se o equipamento está neste grupo
        const hasEquipment = group.equipments?.some((eq: any) => eq.equipment?.id === equipmentId);
        if (hasEquipment) {
            return group;
        }

        // Busca recursivamente nos subgrupos
        if (group.subGroups && group.subGroups.length > 0) {
            const foundInSubGroup = findGroupByEquipmentId(group.subGroups, equipmentId);
            if (foundInSubGroup) {
                return foundInSubGroup;
            }
        }
    }

    return null;
};

const ThermalRegistrationDataInfo = ({
    mode,
    anomalyRecord,
    powerStations,
    equipmentsFiltered,
    inspectionRoutesFiltered,
    thermographers,
    equipmentType,
    onSelectChange,
    onInputChange,
}: ThermalRegistrationDataInfoProps) => {
    const { t, i18n } = useTranslation();
    const tt = (key: string, options?: Record<string, unknown>) => t(key, { ns: "translation", ...options });
    // VIEW: controla somente disable
    const isView = mode === "view";

    // REPORT: controla a forma diferente de visualizar (ValueText + layout)
    const isReport = mode === "report";

    const plantId = anomalyRecord?.plant?.id ?? "";
    const equipmentId = anomalyRecord?.equipment?.id ?? "";
    const routeId = anomalyRecord?.route?.id ?? -1;
    const thermographerId = anomalyRecord?.createdBy?.id;

    const dbService = DatabaseService.getInstance();

    const inspectionRecords = inspectionRoutesFiltered;

    const ValueText = ({ children }: { children: React.ReactNode }) => (
        <span className="thermo-report__value" style={valueTextStyle}>
            {children ?? "--"}
        </span>
    );

    // Helpers p/ pegar "texto" dos selects quando estiver em report
    const powerStationName =
        powerStations.find((p) => p.id === plantId)?.name ?? "--";

    let equipmentLocation = "--";
    try {
        const equipment = equipmentsFiltered.find((e) => e.id === equipmentId);
        
        if (equipment?.id && inspectionRoutesFiltered.length > 0) {
            // Procura o grupo que contém o equipamento em todas as rotas de inspeção
            for (const route of inspectionRoutesFiltered) {
                const foundGroup = findGroupByEquipmentId(route.groups, equipment.id);
                if (foundGroup?.name) {
                    equipmentLocation = foundGroup.name;
                    break;
                }
            }
        }
        
        // Fallback: tenta usar o nome do grupo do equipamento diretamente
        if (equipmentLocation === "--" && equipment?.group?.name) {
            equipmentLocation = equipment.group.name;
        }
    } catch (error) {
        console.error("Error finding equipment location:", error);
    }

    const routeNumber = inspectionRecords.find((r) => r?.id === routeId)?.name ?? "--";
    const thermographerName = thermographers.find((t: any) => t.id === anomalyRecord?.createdBy?.id)?.name ?? "--";
    const reportDateFormatted = useMemo(() => {
        if (!anomalyRecord?.createdAt) return "--";

        const createdAt = new Date(anomalyRecord.createdAt);
        if (Number.isNaN(createdAt.getTime())) return "--";

        return new Intl.DateTimeFormat(getDateLocaleConfig(i18n.language).intlLocale, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
        }).format(createdAt);
    }, [anomalyRecord?.createdAt, i18n.language]);

    return (
        <Form.Group className="bg-body px-2" controlId="exampleForm.ControlTextarea1">
            <div
                style={{
                    display: isReport ? "flex" : "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
                    columnGap: 16,
                    rowGap: 8,
                    width: "100%",
                }}
            >
                {/* COLUNA 1 */}
                <div>
                    <Table borderless hover size="sm" className="mb-0">
                        <tbody>
                            {/* Instalação */}
                            <tr>
                                <td className="bg-100" style={labelCellStyle}>
                                    <Form.Label className="mb-0">
                                        {tt("pages.thermography.thermalRegistrationDataInfo.installation")}
                                    </Form.Label>
                                </td>
                                <td>
                                    {isReport ? (
                                        <ValueText>{powerStationName}</ValueText>
                                    ) : (
                                        <>
                                            <Form.Select
                                                name="plantId"
                                                value={plantId}
                                                onChange={onSelectChange}
                                                required
                                                disabled={isView || mode === "edit_trr"}
                                            >
                                                <option value="">
                                                    {tt("pages.thermography.thermalRegistrationDataInfo.selectSubstation")}
                                                </option>
                                                {powerStations.map((powerStation) => (
                                                    <option key={powerStation.id} value={powerStation.id} data-type="string">
                                                        {powerStation.name}
                                                    </option>
                                                ))}
                                            </Form.Select>

                                            <Form.Control.Feedback type="invalid">
                                                {tt("pages.thermography.thermalRegistrationDataInfo.selectSubstationFeedback")}
                                            </Form.Control.Feedback>
                                        </>
                                    )}
                                </td>
                            </tr>
                            {/* Localização */}
                            <tr>
                                <td style={labelCellStyle}>
                                    <Form.Label className="mb-0">
                                        {tt("pages.thermography.thermalRegistrationDataInfo.location")}
                                    </Form.Label>
                                </td>
                                <td>
                                    {isReport ? (
                                        <ValueText>{equipmentLocation}</ValueText>
                                    ) : (
                                        <Form.Select
                                            name="equipmentId"
                                            value={equipmentId}
                                            onChange={onSelectChange}
                                            required
                                            disabled={(equipmentsFiltered.length === 0 || isView) || mode === "edit_trr"}
                                        >
                                            <option value="">
                                                {tt("pages.thermography.thermalRegistrationDataInfo.selectEquipment")}
                                            </option>
                                            {equipmentsFiltered.map((equipment) => (
                                                <option key={equipment.id} value={equipment.id} data-type="string">
                                                    {equipment.code}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    )}
                                </td>
                            </tr>

                            {/* Equipamento */}
                            <tr>
                                <td style={labelCellStyle}>
                                    <Form.Label className="mb-0">
                                        {tt("pages.thermography.thermalRegistrationDataInfo.equipment")}
                                    </Form.Label>
                                </td>
                                <td>
                                    {isReport ? (
                                        <ValueText>{equipmentType || "--"}</ValueText>
                                    ) : (
                                        <Form.Control
                                            required
                                            type="text"
                                            value={equipmentType}
                                            disabled={(anomalyRecord.equipmentId === "" || isView) || mode === "edit_trr"}
                                            readOnly
                                        />
                                    )}
                                </td>
                            </tr>

                            {/* Rota */}
                            <tr>
                                <td style={labelCellStyle}>
                                    <Form.Label className="mb-0">
                                        {tt("pages.thermography.thermalRegistrationDataInfo.inspectionRecord")}
                                    </Form.Label>
                                </td>
                                <td>
                                    {isReport ? (
                                        <ValueText>
                                            {anomalyRecord?.route?.id === -1
                                                ? tt("pages.thermography.thermalRegistrationDataInfo.noLinkedRoute")
                                                : routeNumber}
                                        </ValueText>
                                    ) : (
                                        <Form.Select
                                            name="routeId"
                                            value={routeId}
                                            onChange={onSelectChange}
                                            disabled={(inspectionRecords?.length === 0 || isView) || mode === "edit_trr"}
                                        >
                                            <option value="-1">
                                                {tt("pages.thermography.thermalRegistrationDataInfo.noLinkedRoute")}
                                            </option>
                                            {inspectionRecords?.map((inspectionRecord) => (
                                                <option key={inspectionRecord.id} value={inspectionRecord.id} data-type="string">
                                                    {inspectionRecord.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                </div>

                {/* COLUNA 2 */}
                <div>
                    <Table borderless hover size="sm" className="mb-0">
                        <tbody>
                            {/* No Relatório */}
                            <tr>
                                <td style={labelCellStyle}>
                                    <Form.Label className="mb-0">
                                        {tt("pages.thermography.thermalRegistrationDataInfo.inReport")}
                                    </Form.Label>
                                </td>
                                <td>
                                    {isReport ? (
                                        <ValueText>{anomalyRecord.name || "--"}</ValueText>
                                    ) : (
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            value={anomalyRecord.name}
                                            onChange={onInputChange}
                                            required
                                            disabled
                                        />
                                    )}
                                </td>
                            </tr>

                            {/* Data Relatório */}
                            <tr>
                                <td style={labelCellStyle}>
                                    <Form.Label className="mb-0">
                                        {tt("pages.thermography.thermalRegistrationDataInfo.reportDate")}
                                    </Form.Label>
                                </td>
                                <td>
                                    {isReport ? (
                                        <ValueText>{reportDateFormatted}</ValueText>
                                    ) : (
                                        <Form.Control
                                            required
                                            type="text"
                                            value={reportDateFormatted}
                                            disabled={isView}
                                            readOnly
                                        />
                                    )}
                                </td>
                            </tr>

                            {/* Ordem de Serviço */}
                            <tr>
                                <td style={labelCellStyle}>
                                    <Form.Label className="mb-0">
                                        {tt("pages.thermography.thermalRegistrationDataInfo.serviceOrder")}
                                    </Form.Label>
                                </td>
                                <td>
                                    {isReport ? (
                                        <ValueText>{anomalyRecord.serviceOrder || "--"}</ValueText>
                                    ) : (
                                        <Form.Control
                                            type="text"
                                            name="serviceOrder"
                                            value={anomalyRecord.serviceOrder}
                                            onChange={onInputChange}
                                            disabled={isView}
                                        />
                                    )}
                                </td>
                            </tr>

                            {/* Termografista */}
                            <tr>
                                <td style={labelCellStyle}>
                                    <Form.Label className="mb-0">
                                        {tt("pages.thermography.thermalRegistrationDataInfo.thermographer")}
                                    </Form.Label>
                                </td>
                                <td>
                                    {isReport ? (
                                        <ValueText>{thermographerName}</ValueText>
                                    ) : (
                                        <Form.Select
                                            name="thermographerId"
                                            value={thermographerId}
                                            onChange={onSelectChange}
                                            required
                                            disabled={isView}
                                        >
                                            <option value="">
                                                {tt("pages.thermography.thermalRegistrationDataInfo.selectThermographer")}
                                            </option>
                                            {thermographers.map((thermographer: any) => (
                                                <option key={thermographer.id} value={thermographer.id} data-type="string">
                                                    {thermographer.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    )}
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                </div>
            </div>
        </Form.Group>
    );
};

export default ThermalRegistrationDataInfo;
